import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { isFuture } from 'date-fns';

import './ContestOverview.scss';
import prizeImage from './../../assets/img/egg/egg-prize.png';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { RandomDrawIcon } from 'components/Icons/Icons';
import { BronzeMedalIcon, GoldMedalIcon, SilverMedalIcon } from 'icons/medals';
import { Select } from '../Select/Select';
import { Contest } from '../../contexts/ContestContext';

export type ContestOverviewProps = {
  title: string;
  description: string;
  game: string;
  cta: JSX.Element;
  icon: JSX.Element;
  selectedContest: Contest;
  contests: Contest[];
  onContestSelect(contestId: number): void;
};

function ContestSwitch({
  contests,
  selectedContest,
  onContestSelect,
}: Pick<ContestOverviewProps, 'contests' | 'onContestSelect' | 'selectedContest'>) {
  const items = contests.map((contest) => ({
    id: String(contest.version),
    text: (
      <div className="egg-contest-select-item">
        <b className="egg-contest-select-item-title">Contest #{contest.version + 1}</b>
        {isFuture(contest.endDate) ? (
          <span className="egg-contest-select-item-status">(Ongoing)</span>
        ) : (
          <span className="egg-contest-select-item-status">(Ended)</span>
        )}
      </div>
    ),
  }));

  const selected = items.find((contest) => Number(contest.id) === selectedContest.version)!;
  return (
    <Select
      isSpaced
      value={selected}
      onSelect={(item) => {
        const contest = contests.find((c) => Number(item.id) === c.version)!;
        onContestSelect(contest.version);
      }}
      items={items}
      defaultId={String(contests[0].version)}
      title="Choose Contest"
    />
  );
}

export const ContestOverview = ({
  title,
  description,
  icon,
  game,
  cta,
  contests,
  selectedContest,
  onContestSelect,
}: ContestOverviewProps) => {
  return (
    <div className="egg-contest-card">
      <div className="egg-contest-header">
        <div className="egg-contest-title-section">
          <div className="egg-content-icon">{icon}</div>
          <ContestSwitch
            contests={contests}
            onContestSelect={onContestSelect}
            selectedContest={selectedContest}
          />
        </div>
        <h3>{title}</h3>
        <p>{description}</p>
        <div className="contest-cta">{cta}</div>
        <div className="contest-cta-mobile">{game} is not available on mobile devices</div>
      </div>
      <HorizontalDivider />
      <div>
        <h3>Prizes</h3>
        <div className="egg-contest-prize-list">
          <ContestPrize
            totalPrizes={2}
            description="2x Free Egg NFT"
            qualification={
              <div>
                <GoldMedalIcon />
                <span className="gold-medal"> 1ST </span>
              </div>
            }
          />

          <ContestPrize
            totalPrizes={1}
            description="1x Free Egg NFT"
            qualification={
              <div>
                <SilverMedalIcon /> <BronzeMedalIcon />
                <span className="silver-medal"> 2ND & </span>
                <span className="bronze-medal"> 3RD </span>
              </div>
            }
          />
          <ContestPrize
            totalPrizes={1}
            description="1x Free Egg NFT"
            qualification={
              <div>
                <RandomDrawIcon />
                <span className="contest-random">Top 100 Random Draw (#4-100) </span>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

type ContestPrizeProps = {
  totalPrizes: number;
  description: string;
  qualification: JSX.Element;
};

const ContestPrize = ({ totalPrizes, description, qualification }: ContestPrizeProps) => {
  return (
    <div className="contest-prize">
      <div className="prize-image">
        {Array.from({ length: totalPrizes }, (_, i) => (
          <LazyLoadImage src={prizeImage} key={i} alt="Egg" />
        ))}
      </div>
      <div className="prize-description">
        <div className="prize-medal">{qualification}</div>
        <h4>{description}</h4>
      </div>
    </div>
  );
};
